import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CompanyService } from 'src/app/company/company.service';
import { IOperatorClass } from './OperatorClass';
import { OperatorInput, OperatorOutput } from './models/models';
import { Labels } from 'src/app/shared/labels';

const FIXED_COLUMNS = 3;
const DIRECT_SHEET_INDEX = 3;
const SUPPLEMENTAL_SHEET_INDEX = 4;

export class PremiumReportOperator implements IOperatorClass {
  private readonly companyService = inject(CompanyService);

  private getCellValue(worksheet: any, row: number, col: number): any {
    const value = worksheet.rows(row).cells(col).value;
    return value !== undefined ? value : null;
  }

  private readSheetData(worksheet: any, columnCount: number): any[] {
    const data: any[] = [];
    let rowIndex = 1;
    while (true) {
      const firstCell = this.getCellValue(worksheet, rowIndex, 0);
      if (firstCell === null || String(firstCell).trim() === '') break;

      const row: Record<number, any> = {};
      for (let col = 0; col < columnCount; col++) {
        const raw = this.getCellValue(worksheet, rowIndex, col);
        if (col < FIXED_COLUMNS) {
          row[col] = raw;
        } else {
          const num = raw !== null && raw !== '' ? parseFloat(String(raw)) : null;
          row[col] = num !== null && !isNaN(num) ? Math.round(num) : null;
        }
      }
      data.push(row);
      rowIndex++;
    }
    return data;
  }

  async runOperator(operatorInput: OperatorInput): Promise<OperatorOutput> {
    const workbook = operatorInput.workbook;
    if (!workbook) {
      return { status: 'error', message: ['Workbook not found.'], operatorInput };
    }

    const directSheet = workbook.worksheets(DIRECT_SHEET_INDEX);
    const supplementalSheet = workbook.worksheets(SUPPLEMENTAL_SHEET_INDEX);

    if (!directSheet) {
      return { status: 'error', message: [`${Labels.premium.directPlural} sheet not found.`], operatorInput };
    }
    if (!supplementalSheet) {
      return { status: 'error', message: [`${Labels.premium.supplementalPlural} sheet not found.`], operatorInput };
    }

    const premiumData$ = this.companyService.getPremiumReportData(
      operatorInput.prfnumber,
      operatorInput.prfyear,
      operatorInput.prfGroup,
    );
    if (!premiumData$) {
      return { status: 'error', message: ['Failed to load premium report data.'], operatorInput };
    }

    let resp: any;
    try {
      resp = await firstValueFrom(premiumData$);
    } catch {
      return { status: 'error', message: ['Failed to load premium report data.'], operatorInput };
    }

    if (resp?.message !== 'Success') {
      return { status: 'error', message: ['Failed to load premium report data.'], operatorInput };
    }

    const columns: any[] = resp['data']?.['columns'] ?? [];
    const rows: any[] = resp['data']?.['rows'] ?? [];
    const udPremiumDirect: any[] = resp['data']?.['userdefined_premium']?.['dpremium'] ?? [];
    const udPremiumSupplemental: any[] = resp['data']?.['userdefined_premium']?.['spremium'] ?? [];
    const udColumns: any[] = resp['data']?.['userdefined_columns'] ?? [];

    const directPremium = this.readSheetData(directSheet, columns.length);
    const supplementalPremium = this.readSheetData(supplementalSheet, columns.length);

    const griddata = {
      premreportlobs: columns,
      premreportstates: rows,
      dpremiuminfo: directPremium,
      spremiuminfo: supplementalPremium,
      udpremiuminfo: udPremiumDirect,
      uspremiuminfo: udPremiumSupplemental,
      udlobs: udColumns,
    };

    const save$ = this.companyService.updatepremiumdetails(
      operatorInput.prfyear,
      operatorInput.prfnumber ?? '',
      griddata,
      operatorInput.prfGroup ?? '',
      false,
      true,
    );
    if (!save$) {
      return { status: 'error', message: ['Failed to save premium report data.'], operatorInput };
    }

    let saveResp: any;
    try {
      saveResp = await firstValueFrom(save$);
    } catch {
      return { status: 'error', message: ['Failed to save premium report data.'], operatorInput };
    }

    if (saveResp?.message !== 'Success') {
      return {
        status: 'error',
        message: [saveResp?.status_msg ?? 'Failed to save premium report data.'],
        operatorInput,
      };
    }

    return { status: 'success', operatorInput };
  }
}

